/**
 * Карточка объявления о грузе — блок `WEB-2.1`, требования 301 (показ
 * расчётов сервера), 302, 303, 304, 305 (половиной), 306 и 319.
 *
 * Карточка читает три операции и показывает их ответы как пришли.
 *
 * Объявление: километраж, страны транзита и число границ считает сервер
 * (серверное требование 502) — в исходнике нет ни координат, ни формулы, и
 * другое число сервера даёт другое число на экране. Имена городов — из ответа,
 * на языке ответа.
 *
 * Входящие offer'ы — **каждый**, в порядке ответа, истёкшие и отклонённые
 * тоже: фирма и «документы проверены», согласие на условия или своя цена
 * (`counter`), число машин, цена offer'а **рядом** с ценой объявления двумя
 * суммами, срок ответа и статус подписью. Разницы, процента и «выгоднее» нет:
 * цены не сравниваются (CR-003 §1). «Одобрить» и «Отклонить» стоят у offer'а,
 * ждущего решения, и только при `manageable` объявления (решение формы `Б9`):
 * значение ожидания названо в файле один раз, остальные статусы — подписью.
 * Истёк ли offer, клиент по часам не судит: статус и отказ `409` приходят от
 * сервера, и после любого ответа на действие карточка перечитывается.
 *
 * Доска — **каждый** слот ответа: номер, статус подписью, номер работы у слота
 * с работой (до блока `WEB-3.1` без перехода — маршрута работы ещё нет).
 * Работы слотов не запрашиваются (решение формы `Р3` А). Над доской — число
 * слотов в каждом статусе перечня, одним проходом по ответу; значение вне
 * перечня считается в «неизвестно» (CR-007). «Требует внимания» здесь нет —
 * этот признак считает сервер, и его ещё нет в контракте (`Р2` А).
 *
 * Состояние встаёт по единственному правилу места (долг `TD-66`): ответа нет —
 * на месте экрана, и отказ прав с отсутствием объявления дают побайтово один
 * экран; offer'ов нет — «пусто» на месте их списка, объявление и доска видны.
 */

import { ApiFailure } from '../api/client';
import type { ApiClient } from '../api/client';
import { nextPageCursor } from '../api/contract';
import type { CargoListing, CargoOffer, CargoSlot } from '../api/contract';
import { CARGO_SLOT_STATUSES } from '../api/generated/enums';
import {
  activeLocale,
  bodyTypeStringKey,
  cargoOfferStatusStringKey,
  cargoSlotStatusStringKey,
  listingStatusStringKey,
  paymentMethodStringKey,
  paymentMomentStringKey,
} from '../i18n';
import type { Strings } from '../i18n';
import { cargoListingEditPath, cargoListingsPath } from '../router';
import {
  dateLabel,
  enumLabel,
  momentTimeLabel,
  moneyLabel,
  placeState,
  refusalText,
  screenStateFor,
} from './state';
import type { ScreenState } from './state';

export interface CargoListingScreenOptions {
  readonly client: ApiClient;
  readonly strings: Strings;
  readonly companyId: string;
  readonly listingId: string;
}

export interface CargoListingScreen {
  readonly element: HTMLElement;
  /** Состояние экрана либо `null` — содержимое показано. */
  readonly state: ScreenState | null;
  load(): Promise<void>;
}

/**
 * Значение статуса offer'а, ждущего решения логиста, — **единственное**
 * значение статуса, названное в этом файле (решение формы `Б9`). Оно решает
 * не «можно ли», а «предлагать ли кнопки»: одобрение и отклонение всё равно
 * судит сервер, и offer, истёкший между чтением и нажатием, получит `409`.
 */
const AWAITING_DECISION: CargoOffer['status'] = 'PENDING';

export function createCargoListingScreen(options: CargoListingScreenOptions): CargoListingScreen {
  const { client, strings, companyId, listingId } = options;

  const element = document.createElement('section');
  const backLink = document.createElement('a');
  const heading = document.createElement('h1');
  const notice = document.createElement('p');
  const offersHeading = document.createElement('h2');
  const offersList = document.createElement('ul');
  const moreOffers = document.createElement('button');
  const slotsHeading = document.createElement('h2');
  const summary = document.createElement('p');
  const slotsList = document.createElement('ul');

  element.className = 'screen screen-cargo-listing';

  backLink.className = 'company-link';
  backLink.href = cargoListingsPath(companyId);
  backLink.textContent = strings.cargo_listings_title;

  notice.className = 'notice';
  notice.setAttribute('role', 'alert');

  offersHeading.textContent = strings.cargo_offers_title;

  moreOffers.type = 'button';
  moreOffers.className = 'action';
  moreOffers.textContent = strings.cargo_offer_more;

  slotsHeading.textContent = strings.cargo_slots_title;
  summary.className = 'cargo-slot-summary';

  let state: ScreenState | null = 'loading';
  /** Пришёл ли ответ сервера — величина правила места состояния. */
  let answered = false;
  let listing: CargoListing | null = null;
  let offers: readonly CargoOffer[] = [];
  let offersCursor: string | null = null;
  let slots: readonly CargoSlot[] = [];
  /**
   * Действие логиста в пути — до ответа сервера кнопки действий неактивны, и
   * второе нажатие второго запроса не шлёт (находка ревью Р6): второе
   * отклонение получило бы `409`, и над карточкой встал бы отказ, хотя
   * действие удалось.
   */
  let acting = false;
  /** Следующая страница offer'ов в пути — та же страница не просится дважды. */
  let loadingMoreOffers = false;

  function line(text: string): HTMLElement {
    const node = document.createElement('p');

    node.textContent = text;

    return node;
  }

  /** Поля объявления — как пришли; пустое поле ответа — пустое место. */
  function facts(shown: CargoListing): HTMLElement[] {
    const locale = activeLocale();
    const lines: HTMLElement[] = [
      line(enumLabel(strings, listingStatusStringKey(shown.status))),
      line(
        [
          `${strings.cargo_listing_form_loading_at}: ${dateLabel(locale, shown.loading_at)}`,
          `${strings.cargo_listing_form_unload_by}: ${dateLabel(locale, shown.unload_by)}`,
        ].join(' · '),
      ),
    ];

    // Километраж, транзит и границы — расчёт сервера; границ нет там, где
    // сухопутной дороги нет (`null`), и тогда строки о них нет вовсе.
    const route: string[] = [`${shown.distance_km} ${strings.cargo_listing_km}`];

    if (shown.transit_countries.length > 0) {
      route.push(`${strings.cargo_listing_transit}: ${shown.transit_countries.join(', ')}`);
    }

    if (shown.border_crossings !== null) {
      route.push(`${strings.cargo_listing_borders}: ${shown.border_crossings}`);
    }

    lines.push(line(route.join(' · ')));
    lines.push(
      line(
        `${strings.cargo_listing_slots_free}: ${shown.slots_free} ${strings.cargo_listing_slots_of} ${shown.slots_total}`,
      ),
    );

    const cargo: string[] = [
      shown.cargo_type,
      `${shown.weight_kg} ${strings.cargo_listing_kg}`,
      `${strings.cargo_listing_form_body_type}: ${enumLabel(strings, bodyTypeStringKey(shown.body.type))}`,
    ];

    if (shown.adr) {
      cargo.push(strings.cargo_listing_form_adr);
    }

    lines.push(line(cargo.join(' · ')));

    const terms: string[] = [];

    if (shown.price !== null) {
      terms.push(`${strings.cargo_listing_form_price}: ${moneyLabel(strings, shown.price)}`);
    }

    terms.push(shown.negotiable ? strings.cargo_listing_form_negotiable : strings.cargo_listing_fixed);

    const payment = [
      enumLabel(strings, paymentMethodStringKey(shown.payment.method)),
      enumLabel(strings, paymentMomentStringKey(shown.payment.moment)),
    ];

    if (shown.payment.deferral_days !== null) {
      payment.push(`${shown.payment.deferral_days} ${strings.cargo_listing_days}`);
    }

    terms.push(`${strings.cargo_listing_payment}: ${payment.join(', ')}`);
    lines.push(line(terms.join(' · ')));

    if (shown.comment !== null) {
      lines.push(line(shown.comment));
    }

    return lines;
  }

  function actions(shown: CargoListing): HTMLElement[] {
    // Правка и закрытие — ровно при `manageable`: кто вправе, считает сервер.
    if (!shown.manageable) {
      return [];
    }

    const edit = document.createElement('a');
    const close = document.createElement('button');

    edit.className = 'company-link';
    edit.href = cargoListingEditPath(companyId, shown.id);
    edit.textContent = strings.cargo_listing_edit;

    close.type = 'button';
    close.className = 'action';
    close.textContent = strings.cargo_listing_close;
    close.disabled = acting;
    close.addEventListener('click', () => {
      if (acting) {
        return;
      }

      if (window.confirm(strings.cargo_listing_close_confirm)) {
        void act(() => client.closeCargoListing(shown.id));
      }
    });

    return [edit, close];
  }

  function offerRow(offer: CargoOffer, shown: CargoListing): HTMLElement {
    const node = document.createElement('li');
    const party = document.createElement('span');
    const terms = document.createElement('span');

    node.className = 'cargo-offer-row';

    const partyParts = [offer.company.name];

    if (offer.company.documents_verified) {
      partyParts.push(strings.cargo_offer_documents_verified);
    }

    party.textContent = partyParts.join(' · ');

    // Две суммы рядом, каждая со своим кодом валюты сервера, — без разницы и
    // процента: цены не сравниваются клиентом (CR-003 §1).
    const prices = [`${strings.cargo_offer_price}: ${moneyLabel(strings, offer.price)}`];

    if (shown.price !== null) {
      prices.push(`${strings.cargo_offer_listing_price}: ${moneyLabel(strings, shown.price)}`);
    }

    terms.textContent = [
      offer.counter ? strings.cargo_offer_counter : strings.cargo_offer_agrees,
      `${strings.cargo_offer_vehicles}: ${offer.vehicles_count}`,
      ...prices,
      `${strings.cargo_offer_expires}: ${momentTimeLabel(activeLocale(), offer.expires_at)}`,
      enumLabel(strings, cargoOfferStatusStringKey(offer.status)),
    ].join(' · ');

    node.append(party, terms);

    if (shown.manageable && offer.status === AWAITING_DECISION) {
      const accept = document.createElement('button');
      const decline = document.createElement('button');

      accept.type = 'button';
      accept.className = 'action';
      accept.textContent = strings.cargo_offer_accept;
      accept.disabled = acting;
      accept.addEventListener('click', () => {
        void act(() => client.acceptCargoOffer(offer.id));
      });

      decline.type = 'button';
      decline.className = 'action';
      decline.textContent = strings.cargo_offer_decline;
      decline.disabled = acting;
      decline.addEventListener('click', () => {
        if (acting) {
          return;
        }

        if (window.confirm(strings.cargo_offer_decline_confirm)) {
          void act(() => client.declineCargoOffer(offer.id));
        }
      });

      node.append(accept, decline);
    }

    return node;
  }

  function slotRow(slot: CargoSlot): HTMLElement {
    const node = document.createElement('li');
    const parts = [`№ ${slot.number}`, enumLabel(strings, cargoSlotStatusStringKey(slot.status))];

    // Номер работы — поле слота: иного номера контракт не даёт, а перехода нет
    // до блока `WEB-3.1`. Слот с offer'ом без работы помечен своим статусом.
    if (slot.work_id !== null) {
      parts.push(`${strings.cargo_slot_work} ${slot.work_id}`);
    }

    node.className = 'cargo-slot-row';
    node.textContent = parts.join(' · ');

    return node;
  }

  /**
   * Сколько слотов в каждом статусе — один проход по ответу. Статусы перечня,
   * которых в ответе нет, показаны нулём; значение вне перечня считается в
   * «неизвестно» и показывается, только когда такое пришло.
   */
  function summaryText(): string {
    const counts = new Map<string, number>();
    let unknown = 0;

    for (const value of CARGO_SLOT_STATUSES) {
      counts.set(cargoSlotStatusStringKey(value), 0);
    }

    for (const slot of slots) {
      const key = cargoSlotStatusStringKey(slot.status);
      const seen = counts.get(key);

      if (seen === undefined) {
        unknown += 1;
      } else {
        counts.set(key, seen + 1);
      }
    }

    const parts: string[] = [];

    for (const value of CARGO_SLOT_STATUSES) {
      const key = cargoSlotStatusStringKey(value);

      parts.push(`${enumLabel(strings, key)}: ${counts.get(key) ?? 0}`);
    }

    if (unknown > 0) {
      parts.push(`${strings.enum_unknown}: ${unknown}`);
    }

    return parts.join(' · ');
  }

  function render(): void {
    const frame: HTMLElement[] = [backLink];

    if (listing !== null) {
      const offerRows: HTMLElement[] = [];
      const slotRows: HTMLElement[] = [];

      heading.textContent = `${listing.origin} → ${listing.destination}`;

      for (const offer of offers) {
        offerRows.push(offerRow(offer, listing));
      }

      for (const slot of slots) {
        slotRows.push(slotRow(slot));
      }

      offersList.replaceChildren(...offerRows);
      slotsList.replaceChildren(...slotRows);
      summary.textContent = summaryText();
      frame.push(heading, ...facts(listing), ...actions(listing));
    }

    frame.push(notice, offersHeading);

    const after: HTMLElement[] = [];

    if (offersCursor !== null) {
      after.push(moreOffers);
    }

    after.push(slotsHeading, summary, slotsList);

    placeState(element, { state, answered, frame, list: offersList, after }, strings);
  }

  /** Три чтения карточки разом: объявление, первая страница offer'ов, доска. */
  async function read(): Promise<void> {
    const [received, page, board] = await Promise.all([
      client.getCargoListing(listingId),
      client.listCargoListingOffers(listingId),
      client.listCargoSlots(listingId),
    ]);

    listing = received;
    offers = page.data;
    offersCursor = nextPageCursor(page.meta);
    slots = board;
    answered = true;
    state = screenStateFor({ kind: 'done', empty: offers.length === 0 });
  }

  async function load(): Promise<void> {
    state = screenStateFor({ kind: 'pending' });
    answered = false;
    listing = null;
    offers = [];
    offersCursor = null;
    slots = [];
    notice.textContent = '';
    render();

    try {
      await read();
    } catch (error) {
      listing = null;
      offers = [];
      offersCursor = null;
      slots = [];
      answered = false;
      state = screenStateFor({
        kind: 'failed',
        code: error instanceof ApiFailure ? error.code : null,
      });
    }

    render();
  }

  /**
   * Перечитать карточку после ответа на действие: offer'ы, доску и само
   * объявление — сервер, а не клиент, знает, сколько слотов свободно и какой
   * offer вытеснен. Отказ перечитывания показанного не уносит.
   */
  async function refresh(): Promise<void> {
    try {
      await read();
    } catch (error) {
      notice.textContent = refusalText(error, strings);
    }

    render();
  }

  /**
   * Действие логиста — закрытие, одобрение, отклонение. Только онлайн (решение
   * формы `Б6`): отказ — текстом на месте действия, экран остаётся на
   * содержимом. Ответил сервер — успехом или отказом — карточка перечитывается
   * после его ответа; обрыв связи ответа не дал, и перечитывать нечего.
   * Пока действие в пути, второе не начинается, а кнопки неактивны (Р6).
   */
  async function act(run: () => Promise<unknown>): Promise<void> {
    if (acting) {
      return;
    }

    acting = true;
    notice.textContent = '';
    render();

    try {
      try {
        await run();
      } catch (error) {
        notice.textContent = refusalText(error, strings);

        if (!(error instanceof ApiFailure) || error.code === null) {
          return;
        }
      }

      await refresh();
    } finally {
      acting = false;
      render();
    }
  }

  async function loadMoreOffers(): Promise<void> {
    if (offersCursor === null || loadingMoreOffers) {
      return;
    }

    loadingMoreOffers = true;
    moreOffers.disabled = true;

    try {
      const page = await client.listCargoListingOffers(listingId, { cursor: offersCursor });

      // Страница встаёт **после** прежней в порядке ответа, без отбора
      // повторов (поправка CONTRACT `К1`).
      offers = [...offers, ...page.data];
      offersCursor = nextPageCursor(page.meta);
    } catch (error) {
      notice.textContent = refusalText(error, strings);
    } finally {
      loadingMoreOffers = false;
      moreOffers.disabled = false;
    }

    render();
  }

  moreOffers.addEventListener('click', () => {
    void loadMoreOffers();
  });

  render();

  return {
    element,

    get state(): ScreenState | null {
      return state;
    },

    load,
  };
}
